import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Save, ArrowLeft, Eye, Plus, Trash2, 
  Image, FileText, Ticket, HelpCircle, Users, 
  Sparkles, Palette, Search, Settings, Type
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPageEditor = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const isNew = pageId === "new";

  const [activeTab, setActiveTab] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [mainEvents, setMainEvents] = useState([]);

  const [page, setPage] = useState({
    slug: "",
    title: "",
    subtitle: "",
    tagline: "",
    banner_tag: "",
    date: "",
    time: "",
    location: "",
    address: "",
    hero_image: "",
    hero_video: "",
    hero_badge: "",
    hero_cta_text: "Reserve My Spot — From $79",
    hero_cta_secondary: "Gift This Experience",
    primary_color: "#D4AF37",
    secondary_color: "#0F0F0F",
    background_color: "#FFFFFF",
    font_color: "#1F2937",
    experience_title: "More Than Soap-Making. An Evening You'll Feel.",
    experience_items: [],
    whats_included_title: "Everything. All of It.",
    whats_included_subtitle: "Just show up — we've handled every detail so you can simply enjoy.",
    whats_included: [],
    take_home_title: "Your Take-Home Creation",
    take_home_subtitle: "Artisan Grape Soap, Made by You",
    take_home_items: [],
    perfect_for_title: "Is This For You? Absolutely.",
    perfect_for: [],
    ticket_options: [],
    group_tickets: [],
    faqs: [],
    cta_title: "You've Read This Far.",
    cta_subtitle: "You Already Know You Want In.",
    parking_info: "Free on-site parking",
    dress_code: "Elevated casual",
    duration: "Approximately 3 hours",
    experience_level: "Beginner-friendly",
    urgency_note: "",
    limited_seats_message: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    og_image: "",
    is_active: false,
    main_event_id: ""
  });

  useEffect(() => {
    if (!isNew) {
      fetchPage();
    }
    fetchMainEvents();
  }, [pageId]);

  const fetchMainEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/main-events`);
      setMainEvents(res.data);
    } catch (err) {
      console.error("Failed to load main events:", err);
    }
  };

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-landing-pages/${pageId}`);
      setPage(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error("Failed to load page");
      navigate("/admin/landing-pages");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!page.slug || !page.title) {
      toast.error("Slug and Title are required");
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/admin/event-landing-pages`, page);
        toast.success("Landing page created!");
        navigate(`/admin/landing-pages/${res.data.id || res.data.page?.id}`);
      } else {
        await axios.put(`${API}/admin/event-landing-pages/${pageId}`, page);
        toast.success("Changes saved!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPage(prev => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (field, template) => {
    setPage(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), template]
    }));
  };

  const updateArrayItem = (field, index, updates) => {
    setPage(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, ...updates } : item
      )
    }));
  };

  const removeArrayItem = (field, index) => {
    setPage(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const tabs = [
    { id: "hero", label: "Hero Section", icon: Image },
    { id: "content", label: "Content", icon: Type },
    { id: "tickets", label: "Tickets", icon: Ticket },
    { id: "faq", label: "FAQ", icon: HelpCircle },
    { id: "audience", label: "Audience", icon: Users },
    { id: "design", label: "Design", icon: Palette },
    { id: "seo", label: "SEO", icon: Search }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#0F0F0F] border-b border-[#27272A] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/landing-pages")}
                className="p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#F5F5F0]">
                  {isNew ? "Create Landing Page" : "Edit Landing Page"}
                </h1>
                {!isNew && page.slug && (
                  <p className="text-sm text-[#A1A1AA]">/{page.slug}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isNew && (
                <a
                  href={`/${page.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-[#A1A1AA] hover:text-[#F5F5F0] border border-[#27272A] hover:border-[#52525B] transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </a>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors disabled:opacity-50"
                data-testid="save-landing-page"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap rounded-t ${
                  activeTab === tab.id
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "text-[#A1A1AA] hover:text-[#F5F5F0] hover:bg-[#27272A]"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-w-5xl">
          
          {/* ==================== HERO SECTION TAB ==================== */}
          {activeTab === "hero" && (
            <div className="space-y-6">
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#D4AF37]" />
                  Page Settings
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">URL Slug *</label>
                    <div className="flex items-center">
                      <span className="px-3 py-2.5 bg-[#0F0F0F] border border-r-0 border-[#27272A] text-[#a1a1aa]">/</span>
                      <input
                        type="text"
                        value={page.slug}
                        onChange={(e) => updateField("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                        placeholder="suds-on-the-vine"
                        className="flex-1 px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Status</label>
                    <button
                      onClick={() => updateField("is_active", !page.is_active)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 border transition-colors ${
                        page.is_active
                          ? "bg-green-500/10 border-green-500/30 text-green-400"
                          : "bg-[#0F0F0F] border-[#27272A] text-[#A1A1AA]"
                      }`}
                    >
                      <span>{page.is_active ? "Active (Public)" : "Draft (Hidden)"}</span>
                      {page.is_active ? "✓" : "○"}
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Parent Event</label>
                  <select
                    value={page.main_event_id || ""}
                    onChange={(e) => updateField("main_event_id", e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] focus:outline-none focus:border-[#D4AF37]"
                    data-testid="parent-event-select"
                  >
                    <option value="">No Parent Event</option>
                    {mainEvents.map(me => (
                      <option key={me.id} value={me.id}>{me.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-[#A1A1AA] mt-1">Link this page to a parent event for grouped reporting and guest management</p>
                </div>
              </div>

              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#D4AF37]" />
                  Hero Content
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Banner Text (Top Bar)</label>
                    <input
                      type="text"
                      value={page.banner_tag}
                      onChange={(e) => updateField("banner_tag", e.target.value)}
                      placeholder="Saturday, May 9th, 2026 • Mother's Day Weekend · Limited Spots"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Main Title *</label>
                    <input
                      type="text"
                      value={page.title}
                      onChange={(e) => updateField("title", e.target.value)}
                      placeholder="The Gift She'll Actually Remember"
                      className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] text-xl focus:outline-none focus:border-[#D4AF37]"
                    />
                    <p className="text-xs text-[#a1a1aa] mt-1">Use &lt;span&gt;text&lt;/span&gt; for highlighted text</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Subtitle</label>
                    <input
                      type="text"
                      value={page.subtitle}
                      onChange={(e) => updateField("subtitle", e.target.value)}
                      placeholder="An intimate, instructor-led luxury soap-making experience"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Hero Badge</label>
                    <input
                      type="text"
                      value={page.hero_badge}
                      onChange={(e) => updateField("hero_badge", e.target.value)}
                      placeholder="Perfect for Mother's Day Weekend"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Hero Image URL</label>
                      <input
                        type="text"
                        value={page.hero_image}
                        onChange={(e) => updateField("hero_image", e.target.value)}
                        placeholder="https://..."
                        className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Hero Video URL (optional)</label>
                      <input
                        type="text"
                        value={page.hero_video}
                        onChange={(e) => updateField("hero_video", e.target.value)}
                        placeholder="https://..."
                        className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>

                  {page.hero_image && (
                    <img src={page.hero_image} alt="Hero preview" className="mt-2 max-h-40 rounded border border-[#27272A]" />
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Primary CTA Button</label>
                      <input
                        type="text"
                        value={page.hero_cta_text}
                        onChange={(e) => updateField("hero_cta_text", e.target.value)}
                        placeholder="Reserve My Spot — From $79"
                        className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Secondary CTA Button</label>
                      <input
                        type="text"
                        value={page.hero_cta_secondary}
                        onChange={(e) => updateField("hero_cta_secondary", e.target.value)}
                        placeholder="Gift This Experience"
                        className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">Event Details</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Date</label>
                    <input
                      type="date"
                      value={page.date}
                      onChange={(e) => updateField("date", e.target.value)}
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Time</label>
                    <input
                      type="text"
                      value={page.time}
                      onChange={(e) => updateField("time", e.target.value)}
                      placeholder="6:30 PM – 9:30 PM"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Location</label>
                    <input
                      type="text"
                      value={page.location}
                      onChange={(e) => updateField("location", e.target.value)}
                      placeholder="Blue Quill Community League"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Address</label>
                    <input
                      type="text"
                      value={page.address}
                      onChange={(e) => updateField("address", e.target.value)}
                      placeholder="11304 25 Ave NW, Edmonton, AB"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Parking Info</label>
                    <input
                      type="text"
                      value={page.parking_info}
                      onChange={(e) => updateField("parking_info", e.target.value)}
                      placeholder="Free on-site parking"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Dress Code</label>
                    <input
                      type="text"
                      value={page.dress_code}
                      onChange={(e) => updateField("dress_code", e.target.value)}
                      placeholder="Elevated casual"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Duration</label>
                    <input
                      type="text"
                      value={page.duration}
                      onChange={(e) => updateField("duration", e.target.value)}
                      placeholder="Approximately 3 hours"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Experience Level</label>
                    <input
                      type="text"
                      value={page.experience_level}
                      onChange={(e) => updateField("experience_level", e.target.value)}
                      placeholder="Beginner-friendly"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== CONTENT TAB ==================== */}
          {activeTab === "content" && (
            <div className="space-y-6">
              {/* Experience Section */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  Experience Section
                </h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Title</label>
                  <input
                    type="text"
                    value={page.experience_title}
                    onChange={(e) => updateField("experience_title", e.target.value)}
                    placeholder="More Than Soap-Making. An Evening You'll Feel."
                    className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#F5F5F0]">Experience Features</label>
                  <button
                    onClick={() => addArrayItem("experience_items", { icon: "sparkle", title: "", description: "" })}
                    className="flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#B8943B]"
                  >
                    <Plus className="w-4 h-4" /> Add Feature
                  </button>
                </div>
                <div className="space-y-3">
                  {page.experience_items?.map((item, i) => (
                    <div key={i} className="p-4 bg-[#0F0F0F] border border-[#27272A] rounded">
                      <div className="flex justify-between mb-3">
                        <span className="text-sm text-[#D4AF37]">Feature {i + 1}</span>
                        <button onClick={() => removeArrayItem("experience_items", i)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-3 gap-3">
                        <select
                          value={item.icon}
                          onChange={(e) => updateArrayItem("experience_items", i, { icon: e.target.value })}
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        >
                          <option value="soap">🧼 Soap</option>
                          <option value="wine">🍷 Wine</option>
                          <option value="sparkle">✨ Sparkle</option>
                          <option value="gift">🎁 Gift</option>
                          <option value="heart">❤️ Heart</option>
                          <option value="star">⭐ Star</option>
                        </select>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateArrayItem("experience_items", i, { title: e.target.value })}
                          placeholder="Feature Title"
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateArrayItem("experience_items", i, { description: e.target.value })}
                          placeholder="Description"
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included Section */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">What's Included Section</h2>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Title</label>
                    <input
                      type="text"
                      value={page.whats_included_title}
                      onChange={(e) => updateField("whats_included_title", e.target.value)}
                      placeholder="Everything. All of It."
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Subtitle</label>
                    <input
                      type="text"
                      value={page.whats_included_subtitle}
                      onChange={(e) => updateField("whats_included_subtitle", e.target.value)}
                      placeholder="Just show up..."
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#F5F5F0]">Included Items</label>
                  <button
                    onClick={() => addArrayItem("whats_included", { text: "" })}
                    className="flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#B8943B]"
                  >
                    <Plus className="w-4 h-4" /> Add Item
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {page.whats_included?.map((item, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={item.text}
                        onChange={(e) => updateArrayItem("whats_included", i, { text: e.target.value })}
                        placeholder="Instructor-led soap-making session"
                        className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                      />
                      <button onClick={() => removeArrayItem("whats_included", i)} className="p-2 text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Take Home Section */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">Take-Home Creation Section</h2>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Title</label>
                    <input
                      type="text"
                      value={page.take_home_title}
                      onChange={(e) => updateField("take_home_title", e.target.value)}
                      placeholder="Your Take-Home Creation"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Subtitle</label>
                    <input
                      type="text"
                      value={page.take_home_subtitle}
                      onChange={(e) => updateField("take_home_subtitle", e.target.value)}
                      placeholder="Artisan Grape Soap, Made by You"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-[#F5F5F0]">Product Variants</label>
                  <button
                    onClick={() => addArrayItem("take_home_items", { image: "", name: "", label: "", popular: false })}
                    className="flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#B8943B]"
                  >
                    <Plus className="w-4 h-4" /> Add Variant
                  </button>
                </div>
                <div className="space-y-3">
                  {page.take_home_items?.map((item, i) => (
                    <div key={i} className="p-4 bg-[#0F0F0F] border border-[#27272A] rounded">
                      <div className="flex justify-between mb-3">
                        <span className="text-sm text-[#D4AF37]">Variant {i + 1}</span>
                        <button onClick={() => removeArrayItem("take_home_items", i)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-4 gap-3">
                        <input
                          type="text"
                          value={item.image}
                          onChange={(e) => updateArrayItem("take_home_items", i, { image: e.target.value })}
                          placeholder="Image URL"
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateArrayItem("take_home_items", i, { name: e.target.value })}
                          placeholder="Name (e.g., Rosé Blush)"
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                        <input
                          type="text"
                          value={item.label}
                          onChange={(e) => updateArrayItem("take_home_items", i, { label: e.target.value })}
                          placeholder="Label (e.g., Elegant)"
                          className="px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                        <label className="flex items-center gap-2 px-3 py-2 bg-[#121212] border border-[#27272A] rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.popular || false}
                            onChange={(e) => updateArrayItem("take_home_items", i, { popular: e.target.checked })}
                            className="accent-[#D4AF37]"
                          />
                          <span className="text-sm text-[#A1A1AA]">Popular</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">Final CTA Section</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">CTA Title</label>
                    <input
                      type="text"
                      value={page.cta_title}
                      onChange={(e) => updateField("cta_title", e.target.value)}
                      placeholder="You've Read This Far."
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">CTA Subtitle</label>
                    <input
                      type="text"
                      value={page.cta_subtitle}
                      onChange={(e) => updateField("cta_subtitle", e.target.value)}
                      placeholder="You Already Know You Want In."
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Urgency Note</label>
                    <textarea
                      value={page.urgency_note}
                      onChange={(e) => updateField("urgency_note", e.target.value)}
                      placeholder="Mother's Day Weekend — timing doesn't get better."
                      rows={2}
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Limited Seats Message</label>
                    <textarea
                      value={page.limited_seats_message}
                      onChange={(e) => updateField("limited_seats_message", e.target.value)}
                      placeholder="We keep it small so it stays special."
                      rows={2}
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== TICKETS TAB ==================== */}
          {activeTab === "tickets" && (
            <div className="space-y-6">
              {/* Individual Tickets */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#F5F5F0] flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-[#D4AF37]" />
                    Individual Tickets
                  </h2>
                  <button
                    onClick={() => addArrayItem("ticket_options", { 
                      id: `ticket-${Date.now()}`, name: "", price: 0, description: "", perks: [], status: "available", popular: false, badge: ""
                    })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37] text-[#050505] text-sm font-medium hover:bg-[#B8943B]"
                  >
                    <Plus className="w-4 h-4" /> Add Ticket
                  </button>
                </div>

                <div className="space-y-4">
                  {page.ticket_options?.map((ticket, i) => (
                    <div key={i} className="p-5 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-[#D4AF37] font-medium">Ticket {i + 1}: {ticket.name || "Untitled"}</h3>
                        <button onClick={() => removeArrayItem("ticket_options", i)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Ticket Name *</label>
                          <input
                            type="text"
                            value={ticket.name}
                            onChange={(e) => updateArrayItem("ticket_options", i, { name: e.target.value })}
                            placeholder="Early Bird"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Price ($) *</label>
                          <input
                            type="number"
                            value={ticket.price}
                            onChange={(e) => updateArrayItem("ticket_options", i, { price: parseFloat(e.target.value) || 0 })}
                            placeholder="79"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Original Price</label>
                          <input
                            type="number"
                            value={ticket.original_price || ""}
                            onChange={(e) => updateArrayItem("ticket_options", i, { original_price: parseFloat(e.target.value) || null })}
                            placeholder="95"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Max Qty (Capacity)</label>
                          <input
                            type="number"
                            value={ticket.max_quantity || ""}
                            onChange={(e) => updateArrayItem("ticket_options", i, { max_quantity: parseInt(e.target.value) || null })}
                            placeholder="Unlimited"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                      </div>

                      {/* Inventory Info */}
                      {ticket.max_quantity > 0 && (
                        <div className="mb-4 p-3 bg-[#1a1a1a] border border-[#27272A] rounded">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#A1A1AA]">Inventory:</span>
                            <span className="text-[#D4AF37] font-medium">
                              {ticket.sold_count || 0} / {ticket.max_quantity} sold
                            </span>
                          </div>
                          <div className="mt-2 h-2 bg-[#27272A] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#D4AF37] transition-all"
                              style={{ width: `${Math.min(((ticket.sold_count || 0) / ticket.max_quantity) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-[#a1a1aa] mt-1">
                            {ticket.max_quantity - (ticket.sold_count || 0)} remaining
                          </p>
                        </div>
                      )}

                      <div className="mb-4">
                        <label className="block text-xs text-[#A1A1AA] mb-1">Description</label>
                        <input
                          type="text"
                          value={ticket.description}
                          onChange={(e) => updateArrayItem("ticket_options", i, { description: e.target.value })}
                          placeholder="For the decisive woman who loves a beautiful deal"
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Ticket Status</label>
                          <select
                            value={ticket.status || "available"}
                            onChange={(e) => updateArrayItem("ticket_options", i, { status: e.target.value })}
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          >
                            <option value="available">✅ Available</option>
                            <option value="limited">🔥 Limited Spots</option>
                            <option value="almost_sold_out">⚡ Almost Sold Out</option>
                            <option value="sold_out">❌ Sold Out</option>
                            <option value="coming_soon">🕐 Coming Soon</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Custom Badge</label>
                          <input
                            type="text"
                            value={ticket.badge || ""}
                            onChange={(e) => updateArrayItem("ticket_options", i, { badge: e.target.value })}
                            placeholder="Save $16"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 w-full px-3 py-2 bg-[#121212] border border-[#27272A] rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ticket.popular || false}
                              onChange={(e) => updateArrayItem("ticket_options", i, { popular: e.target.checked })}
                              className="accent-[#D4AF37]"
                            />
                            <span className="text-sm text-[#A1A1AA]">⭐ Most Popular</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-[#A1A1AA] mb-1">Perks (one per line)</label>
                        <textarea
                          value={(ticket.perks || []).join("\n")}
                          onChange={(e) => updateArrayItem("ticket_options", i, { perks: e.target.value.split("\n").filter(Boolean) })}
                          placeholder="Full event experience&#10;All materials included&#10;Take-home soap"
                          rows={4}
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Group Tickets */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#F5F5F0]">Group Tickets</h2>
                  <button
                    onClick={() => addArrayItem("group_tickets", { 
                      id: `group-${Date.now()}`, name: "", price: 0, seats: 2, per_person: 0, savings: "", description: ""
                    })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37] text-[#050505] text-sm font-medium hover:bg-[#B8943B]"
                  >
                    <Plus className="w-4 h-4" /> Add Group
                  </button>
                </div>

                <div className="space-y-4">
                  {page.group_tickets?.map((ticket, i) => (
                    <div key={i} className="p-5 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-[#D4AF37] font-medium">Group {i + 1}: {ticket.name || "Untitled"}</h3>
                        <button onClick={() => removeArrayItem("group_tickets", i)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Name *</label>
                          <input
                            type="text"
                            value={ticket.name}
                            onChange={(e) => updateArrayItem("group_tickets", i, { name: e.target.value })}
                            placeholder="Pair of Petals"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Total Price ($)</label>
                          <input
                            type="number"
                            value={ticket.price}
                            onChange={(e) => updateArrayItem("group_tickets", i, { price: parseFloat(e.target.value) || 0 })}
                            placeholder="149"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Seats</label>
                          <input
                            type="number"
                            value={ticket.seats}
                            onChange={(e) => updateArrayItem("group_tickets", i, { seats: parseInt(e.target.value) || 2 })}
                            placeholder="2"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Savings Badge</label>
                          <input
                            type="text"
                            value={ticket.savings}
                            onChange={(e) => updateArrayItem("group_tickets", i, { savings: e.target.value })}
                            placeholder="Save $41"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Max Groups</label>
                          <input
                            type="number"
                            value={ticket.max_quantity || ""}
                            onChange={(e) => updateArrayItem("group_tickets", i, { max_quantity: parseInt(e.target.value) || null })}
                            placeholder="Unlimited"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                      </div>

                      {/* Inventory Info for Group Tickets */}
                      {ticket.max_quantity > 0 && (
                        <div className="mb-4 p-3 bg-[#1a1a1a] border border-[#27272A] rounded">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#A1A1AA]">Inventory ({ticket.seats || 2} seats/group):</span>
                            <span className="text-[#D4AF37] font-medium">
                              {ticket.sold_count || 0} / {ticket.max_quantity * (ticket.seats || 2)} seats sold
                            </span>
                          </div>
                          <div className="mt-2 h-2 bg-[#27272A] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#D4AF37] transition-all"
                              style={{ width: `${Math.min(((ticket.sold_count || 0) / (ticket.max_quantity * (ticket.seats || 2))) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs text-[#A1A1AA] mb-1">Description</label>
                        <input
                          type="text"
                          value={ticket.description}
                          onChange={(e) => updateArrayItem("group_tickets", i, { description: e.target.value })}
                          placeholder="Perfect for two"
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ==================== FAQ TAB ==================== */}
          {activeTab === "faq" && (
            <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#F5F5F0] flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#D4AF37]" />
                  Frequently Asked Questions
                </h2>
                <button
                  onClick={() => addArrayItem("faqs", { question: "", answer: "" })}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37] text-[#050505] text-sm font-medium hover:bg-[#B8943B]"
                >
                  <Plus className="w-4 h-4" /> Add FAQ
                </button>
              </div>

              <div className="space-y-4">
                {page.faqs?.map((faq, i) => (
                  <div key={i} className="p-4 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm text-[#D4AF37]">FAQ {i + 1}</span>
                      <button onClick={() => removeArrayItem("faqs", i)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-[#A1A1AA] mb-1">Question</label>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => updateArrayItem("faqs", i, { question: e.target.value })}
                          placeholder="Do I need soap-making experience?"
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#A1A1AA] mb-1">Answer</label>
                        <textarea
                          value={faq.answer}
                          onChange={(e) => updateArrayItem("faqs", i, { answer: e.target.value })}
                          placeholder="Not at all! This event is completely beginner-friendly..."
                          rows={3}
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(!page.faqs || page.faqs.length === 0) && (
                  <p className="text-center text-[#a1a1aa] py-8">No FAQs added yet. Click "Add FAQ" to create one.</p>
                )}
              </div>
            </div>
          )}

          {/* ==================== AUDIENCE TAB ==================== */}
          {activeTab === "audience" && (
            <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#F5F5F0] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Perfect For (Target Audience)
                </h2>
                <button
                  onClick={() => addArrayItem("perfect_for", { icon: "👭", title: "", description: "" })}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#D4AF37] text-[#050505] text-sm font-medium hover:bg-[#B8943B]"
                >
                  <Plus className="w-4 h-4" /> Add Audience
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Section Title</label>
                <input
                  type="text"
                  value={page.perfect_for_title}
                  onChange={(e) => updateField("perfect_for_title", e.target.value)}
                  placeholder="Is This For You? Absolutely."
                  className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {page.perfect_for?.map((item, i) => (
                  <div key={i} className="p-4 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm text-[#D4AF37]">Audience {i + 1}</span>
                      <button onClick={() => removeArrayItem("perfect_for", i)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="w-20">
                          <label className="block text-xs text-[#A1A1AA] mb-1">Icon</label>
                          <input
                            type="text"
                            value={item.icon}
                            onChange={(e) => updateArrayItem("perfect_for", i, { icon: e.target.value })}
                            placeholder="👭"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded text-center text-xl"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-[#A1A1AA] mb-1">Title</label>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => updateArrayItem("perfect_for", i, { title: e.target.value })}
                            placeholder="Sisters"
                            className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[#A1A1AA] mb-1">Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateArrayItem("perfect_for", i, { description: e.target.value })}
                          placeholder="Bond over creativity and wine"
                          className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {(!page.perfect_for || page.perfect_for.length === 0) && (
                  <p className="text-center text-[#a1a1aa] py-8 col-span-2">No audiences added yet.</p>
                )}
              </div>
            </div>
          )}

          {/* ==================== DESIGN TAB ==================== */}
          {activeTab === "design" && (
            <div className="space-y-6">
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#D4AF37]" />
                  Color Scheme
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={page.primary_color || "#D4AF37"}
                        onChange={(e) => updateField("primary_color", e.target.value)}
                        className="w-12 h-10 bg-transparent border border-[#27272A] rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={page.primary_color || "#D4AF37"}
                        onChange={(e) => updateField("primary_color", e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                      />
                    </div>
                    <p className="text-xs text-[#a1a1aa] mt-1">Buttons, accents, highlights</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={page.secondary_color || "#0F0F0F"}
                        onChange={(e) => updateField("secondary_color", e.target.value)}
                        className="w-12 h-10 bg-transparent border border-[#27272A] rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={page.secondary_color || "#0F0F0F"}
                        onChange={(e) => updateField("secondary_color", e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                      />
                    </div>
                    <p className="text-xs text-[#a1a1aa] mt-1">Footer, dark sections</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Background Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={page.background_color || "#FFFFFF"}
                        onChange={(e) => updateField("background_color", e.target.value)}
                        className="w-12 h-10 bg-transparent border border-[#27272A] rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={page.background_color || "#FFFFFF"}
                        onChange={(e) => updateField("background_color", e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                      />
                    </div>
                    <p className="text-xs text-[#a1a1aa] mt-1">Page background</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Text Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={page.font_color || "#1F2937"}
                        onChange={(e) => updateField("font_color", e.target.value)}
                        className="w-12 h-10 bg-transparent border border-[#27272A] rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={page.font_color || "#1F2937"}
                        onChange={(e) => updateField("font_color", e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                      />
                    </div>
                    <p className="text-xs text-[#a1a1aa] mt-1">Main body text</p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">Color Preview</h2>
                <div 
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: page.background_color || "#FFFFFF" }}
                >
                  <h3 
                    className="text-2xl font-bold mb-2"
                    style={{ color: page.font_color || "#1F2937" }}
                  >
                    Sample Heading
                  </h3>
                  <p 
                    className="mb-4"
                    style={{ color: page.font_color || "#1F2937", opacity: 0.7 }}
                  >
                    This is how your text will appear on the landing page.
                  </p>
                  <button 
                    className="px-6 py-2 font-semibold rounded"
                    style={{ 
                      backgroundColor: page.primary_color || "#D4AF37",
                      color: page.secondary_color || "#0F0F0F"
                    }}
                  >
                    Primary Button
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== SEO TAB ==================== */}
          {activeTab === "seo" && (
            <div className="space-y-6">
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#D4AF37]" />
                  Search Engine Optimization
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">SEO Title</label>
                    <input
                      type="text"
                      value={page.seo_title}
                      onChange={(e) => updateField("seo_title", e.target.value)}
                      placeholder="The Gift She'll Actually Remember | Mix & Mingle"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                    <p className="text-xs text-[#a1a1aa] mt-1">
                      {(page.seo_title || "").length}/60 characters (recommended)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Meta Description</label>
                    <textarea
                      value={page.seo_description}
                      onChange={(e) => updateField("seo_description", e.target.value)}
                      placeholder="Join us for an intimate, instructor-led luxury soap-making experience. Perfect for Mother's Day Weekend..."
                      rows={3}
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                    <p className="text-xs text-[#a1a1aa] mt-1">
                      {(page.seo_description || "").length}/160 characters (recommended)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Keywords</label>
                    <input
                      type="text"
                      value={page.seo_keywords}
                      onChange={(e) => updateField("seo_keywords", e.target.value)}
                      placeholder="soap making, Mother's Day, Edmonton events, women's night out"
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                    <p className="text-xs text-[#a1a1aa] mt-1">Comma-separated keywords</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F5F5F0] mb-2">Social Share Image (og:image)</label>
                    <input
                      type="text"
                      value={page.og_image}
                      onChange={(e) => updateField("og_image", e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] font-medium placeholder-[#6b7280] focus:outline-none focus:border-[#D4AF37]"
                    />
                    <p className="text-xs text-[#a1a1aa] mt-1">Recommended size: 1200x630px</p>
                    {page.og_image && (
                      <img src={page.og_image} alt="OG preview" className="mt-2 max-h-32 rounded border border-[#27272A]" />
                    )}
                  </div>
                </div>
              </div>

              {/* SEO Preview */}
              <div className="bg-[#121212] border border-[#27272A] p-6 rounded-lg">
                <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4">Google Search Preview</h2>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {page.seo_title || page.title || "Page Title"}
                  </p>
                  <p className="text-green-700 text-sm">
                    mixandmingleevents.com/{page.slug || "page-slug"}
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    {page.seo_description || page.subtitle || "Meta description will appear here..."}
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </AdminLayout>
  );
};

export default LandingPageEditor;
